import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useTheme } from "../contexts/ThemeContext";
import {
  Shield,
  BarChart3,
  Activity,
  FileText,
  ShoppingCart,
  Heart,
  Pill,
  Building2,
  MapPin,
  RefreshCw,
  Loader,
} from "lucide-react";

const AdminAnalyticsPage = ({ user, userRole }) => {
  const { themeClasses } = useTheme();
  const [adminAnalytics, setAdminAnalytics] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(""); // Empty means all currencies

  const getAdminAnalytics = httpsCallable(functions, "getAdminAnalytics");

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    const currencyMap = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      NOK: "kr",
      SEK: "kr",
      DKK: "kr",
      JPY: "¥",
      CNY: "¥",
    };
    return currencyMap[currencyCode] || "$";
  };

  // Get the appropriate currency symbol for display
  const getDisplayCurrency = () => {
    if (selectedCurrency) {
      return getCurrencySymbol(selectedCurrency);
    }
    // If no filter, show mixed currencies warning
    return "$";
  };

  useEffect(() => {
    if (user && userRole === "admin") {
      loadAdminAnalytics();
    }
  }, [user, userRole, selectedCurrency]); // Added selectedCurrency dependency

  const loadAdminAnalytics = async () => {
    if (userRole !== "admin") return;

    try {
      setIsLoadingAnalytics(true);
      setAnalyticsError(null);

      console.log("Loading admin analytics...");
      const requestData = selectedCurrency
        ? { currencyFilter: selectedCurrency }
        : {};
      const result = await getAdminAnalytics(requestData);

      if (result.data.success) {
        setAdminAnalytics(result.data.data);
        console.log("Admin analytics loaded successfully");
      } else {
        throw new Error("Failed to load analytics");
      }
    } catch (error) {
      console.error("Error loading admin analytics:", error);
      setAnalyticsError(error.message);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  if (userRole !== "admin") {
    return (
      <div className="text-center py-12">
        <Shield
          className={`w-16 h-16 mx-auto mb-4 ${themeClasses.text.secondary}`}
        />
        <h2 className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}>
          Access Denied
        </h2>
        <p className={themeClasses.text.secondary}>
          This page is only accessible to administrators
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2
          className={`text-2xl font-bold flex items-center ${themeClasses.text.primary}`}
        >
          <BarChart3 className="w-8 h-8 text-cyan-400 mr-3" />
          Admin Analytics Dashboard
        </h2>
        <div className="flex items-center gap-4">
          {/* Currency Filter */}
          <select
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className={`${themeClasses.bg.input} border ${themeClasses.border.primary} ${themeClasses.text.primary} px-3 py-2 rounded-lg text-sm`}
          >
            <option value="">All Currencies</option>
            <option value="NOK">🇳🇴 Norwegian Kroner (NOK)</option>
            <option value="GBP">🇬🇧 British Pounds (GBP)</option>
            <option value="USD">🇺🇸 US Dollars (USD)</option>
            <option value="EUR">🇪🇺 Euros (EUR)</option>
            <option value="SEK">🇸🇪 Swedish Krona (SEK)</option>
            <option value="DKK">🇩🇰 Danish Kroner (DKK)</option>
          </select>
          <div className="flex items-center gap-4">
            {/* Currency Filter */}

            <button
              onClick={loadAdminAnalytics}
              disabled={isLoadingAnalytics}
              className={`${themeClasses.bg.button} disabled:${themeClasses.bg.tertiary} text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 flex items-center`}
            >
              {isLoadingAnalytics ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Data
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {analyticsError && (
        <div className="bg-red-900/30 border border-red-700 p-4 rounded-lg">
          <p className="text-red-300">
            Error loading analytics: {analyticsError}
          </p>
        </div>
      )}

      {adminAnalytics ? (
        <div className="space-y-8">
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <div className="flex items-center space-x-3">
                <FileText className="w-8 h-8 text-cyan-400" />
                <div>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Total Records
                  </p>
                  <p
                    className={`text-2xl font-bold ${themeClasses.text.primary}`}
                  >
                    {adminAnalytics.overview.totalRecords.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <div className="flex items-center space-x-3">
                <ShoppingCart className="w-8 h-8 text-green-400" />
                <div>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Total Revenue
                  </p>
                  <p
                    className={`text-2xl font-bold ${themeClasses.text.primary}`}
                  >
                    {getDisplayCurrency()}
                    {adminAnalytics.overview.totalRevenue.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <div className="flex items-center space-x-3">
                <Activity className="w-8 h-8 text-purple-400" />
                <div>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Avg Transaction
                  </p>
                  <p
                    className={`text-2xl font-bold ${themeClasses.text.primary}`}
                  >
                    {getDisplayCurrency()}
                    {adminAnalytics.overview.avgTransactionValue.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <div className="flex items-center space-x-3">
                <Heart className="w-8 h-8 text-red-400" />
                <div>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Unique Clinics
                  </p>
                  <p
                    className={`text-2xl font-bold ${themeClasses.text.primary}`}
                  >
                    {adminAnalytics.overview.uniqueClinics}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          {adminAnalytics.topCategories &&
            adminAnalytics.topCategories.length > 0 && (
              <div
                className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
              >
                <h3
                  className={`text-xl font-bold mb-4 flex items-center ${themeClasses.text.primary}`}
                >
                  <BarChart3 className="w-6 h-6 text-cyan-400 mr-2" />
                  Service Categories Overview
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {adminAnalytics.topCategories.map((category, index) => (
                    <div
                      key={index}
                      className={`${themeClasses.bg.secondary} p-4 rounded-lg border ${themeClasses.border.primary}`}
                    >
                      <div
                        className={`font-semibold ${themeClasses.text.primary} mb-2`}
                      >
                        {category.category}
                      </div>
                      <div className="space-y-1">
                        <div
                          className={`text-sm ${themeClasses.text.secondary}`}
                        >
                          {category.count} services • {getDisplayCurrency()}
                          {category.revenue.toFixed(2)} revenue
                        </div>
                        <div
                          className={`text-xs ${themeClasses.text.secondary}`}
                        >
                          {category.uniqueServices} unique types • Avg: $
                          {category.avgPrice.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Top Lists Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Medications */}
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h3
                className={`text-xl font-bold mb-4 flex items-center ${themeClasses.text.primary}`}
              >
                <Pill className="w-6 h-6 text-cyan-400 mr-2" />
                Top 10 Medications
              </h3>
              <div className="space-y-3">
                {adminAnalytics.topMedications.map((medication, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <span className={`text-sm ${themeClasses.text.secondary}`}>
                      {index + 1}. {medication.name}
                    </span>
                    <span
                      className={`${themeClasses.text.accent} font-semibold`}
                    >
                      {medication.count} times
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Procedures */}
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h3
                className={`text-xl font-bold mb-4 flex items-center ${themeClasses.text.primary}`}
              >
                <Activity className="w-6 h-6 text-green-400 mr-2" />
                Top 10 Procedures
              </h3>
              <div className="space-y-3">
                {adminAnalytics.topProcedures.map((procedure, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <span className={`text-sm ${themeClasses.text.secondary}`}>
                      {index + 1}. {procedure.name}
                    </span>
                    <span
                      className={`${themeClasses.text.success} font-semibold`}
                    >
                      {procedure.count} times
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Services */}
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h3
                className={`text-xl font-bold mb-4 flex items-center ${themeClasses.text.primary}`}
              >
                <Activity className="w-6 h-6 text-purple-400 mr-2" />
                Top 10 Services
              </h3>
              <div className="space-y-3">
                {adminAnalytics.topServices.map((service, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <span className={`text-sm ${themeClasses.text.secondary}`}>
                      {index + 1}. {service.name}
                    </span>
                    <div className="text-right">
                      <div
                        className={`${themeClasses.text.accent} font-semibold`}
                      >
                        {service.count} times
                      </div>
                      <div className={`${themeClasses.text.success} text-xs`}>
                        ${service.revenue.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Clinics */}
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h3
                className={`text-xl font-bold mb-4 flex items-center ${themeClasses.text.primary}`}
              >
                <Building2 className="w-6 h-6 text-orange-400 mr-2" />
                Top 10 Clinics by Revenue
              </h3>
              <div className="space-y-3">
                {adminAnalytics.topClinics.map((clinic, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <span className={`text-sm ${themeClasses.text.secondary}`}>
                      {index + 1}. {clinic.name}
                    </span>
                    <div className="text-right">
                      <div
                        className={`${themeClasses.text.success} font-semibold`}
                      >
                        ${clinic.revenue.toFixed(2)}
                      </div>
                      <div className={`text-xs ${themeClasses.text.secondary}`}>
                        {clinic.recordCount} records
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Locations */}
          {adminAnalytics.topLocations.length > 0 && (
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h3
                className={`text-xl font-bold mb-4 flex items-center ${themeClasses.text.primary}`}
              >
                <MapPin className="w-6 h-6 text-red-400 mr-2" />
                Top Locations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {adminAnalytics.topLocations.map((location, index) => (
                  <div
                    key={index}
                    className={`${themeClasses.bg.secondary} p-4 rounded-lg flex justify-between items-center`}
                  >
                    <span className={`text-sm ${themeClasses.text.secondary}`}>
                      {index + 1}. {location.location}
                    </span>
                    <span
                      className={`${themeClasses.text.error} font-semibold`}
                    >
                      {location.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly Revenue Trend */}
          {adminAnalytics.monthlyTrends.length > 0 && (
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h3
                className={`text-xl font-bold mb-4 flex items-center ${themeClasses.text.primary}`}
              >
                <BarChart3 className="w-6 h-6 text-cyan-400 mr-2" />
                Monthly Revenue Trend
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {adminAnalytics.monthlyTrends.map((trend, index) => (
                  <div
                    key={index}
                    className={`${themeClasses.bg.secondary} p-4 rounded-lg`}
                  >
                    <div className={`text-sm ${themeClasses.text.secondary}`}>
                      {trend.month}
                    </div>
                    <div
                      className={`${themeClasses.text.accent} font-bold text-lg`}
                    >
                      ${trend.revenue.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data Generation Info */}
          <div
            className={`${themeClasses.bg.card} rounded-lg border p-4 ${themeClasses.border.primary}`}
          >
            <div
              className={`flex justify-between items-center text-sm ${themeClasses.text.secondary}`}
            >
              <span>
                Generated:{" "}
                {new Date(adminAnalytics.generatedAt).toLocaleString()}
              </span>
              <span>By: {adminAnalytics.generatedBy}</span>
            </div>
          </div>
        </div>
      ) : !isLoadingAnalytics ? (
        <div
          className={`${themeClasses.bg.card} rounded-lg border p-8 text-center ${themeClasses.border.primary}`}
        >
          <BarChart3
            className={`w-16 h-16 mx-auto mb-4 ${themeClasses.text.secondary}`}
          />
          <h3 className={`text-xl font-bold mb-2 ${themeClasses.text.primary}`}>
            Admin Analytics
          </h3>
          <p className={`mb-6 ${themeClasses.text.secondary}`}>
            Click "Refresh Data" to load comprehensive analytics across all
            health records
          </p>
          <button
            onClick={loadAdminAnalytics}
            className={`${themeClasses.bg.button} text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center mx-auto`}
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Load Analytics
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default AdminAnalyticsPage;
